/**
 * Testimonial Slider — crawl speed, and a pause the user can feel.
 *
 * ── Speed ──────────────────────────────────────────────────────────────────
 * The scroll speed is slick's `speed` option, which the plugin writes once into
 * `data-slickwprev`. Its responsive settings only carry slidesToShow and
 * slidesToScroll, so the speed cannot be set per breakpoint in the template.
 * We set it after init instead, through slick's own API.
 *
 * Overriding the track's transition-duration in CSS does NOT work: slick times
 * the end of each move with its own timer from `speed`, so a longer CSS
 * duration makes it start the next move mid-flight, and the crawl stutters.
 *
 * ── Stopping ───────────────────────────────────────────────────────────────
 * Slick's own pauseOnHover only sets `interrupted`, which blocks the NEXT
 * advance (`interrupt()`, `autoPlayIterator()` in wprs-slick.js). The move
 * already running is a CSS transition and carries on to the slide boundary, so
 * a hover reads as "keeps going" for up to a full `speed`.
 *
 * To stop where it is, we pin the track's live transform and stub out the one
 * thing that would move it again — slick's own end-of-transition timer calls
 * postSlide -> setPosition. Letting go runs out whatever distance was left, at
 * the same rate, and hands the crawl back to slick from a settled position, so
 * slick's idea of where the track sits is never wrong for long.
 *
 * Everything here reaches into the plugin through `el.slickwprev`, the instance
 * its jQuery wrapper leaves on the element. Every access is guarded: if a
 * plugin update moves any of it, the slider falls back to its stock behaviour
 * rather than breaking.
 */

const SMALL = window.matchMedia('(max-width: 47.9375rem)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

const SPEED = {
	small: 8500,
	large: 6000,
};

const SLIDERS = '[id^="sections-testimonial-slider-block"] .wprevgoslick';

// After a touch swipe, before the crawl starts again.
const RESUME_AFTER = 1500;

// Mouse travel, in px, before a press counts as a drag rather than a click.
const DRAG_THRESHOLD = 5;

const started = new WeakSet();

/** The X translate of a computed matrix or an inline translate3d, in px. */
function translateX(value) {
	const matrix = /^matrix(3d)?\(([^)]+)\)/.exec(value || '');

	if (matrix) {
		const parts = matrix[2].split(',').map(Number);

		return matrix[1] ? parts[12] : parts[4];
	}

	const translate = /translate3d\((-?[\d.]+)px/.exec(value || '');

	return translate ? Number(translate[1]) : 0;
}

const translate = (x) => `translate3d(${x}px, 0px, 0px)`;

function control(el) {
	const button = el.closest('.testimonial-slider')?.querySelector('[data-tsl-toggle]') ?? null;

	let hovered = false;
	let held = false;
	let paused = REDUCED_MOTION.matches;
	let frozen = false;
	let moved = false;
	let resume = 0;
	let drag = null;
	let target = '';
	let setPosition = null;
	let runout = 0;

	const slick = () => el.slickwprev ?? null;
	const track = () => slick()?.$slideTrack?.[0] ?? null;

	function freeze() {
		const s = slick();
		const t = track();

		if (frozen || !s || !t) {
			return;
		}

		// Where slick was heading. It writes the target inline and lets the CSS
		// transition carry the track there, so this is readable mid-flight.
		target = t.style.transform || '';

		// Slick's own swipe fights the crawl. Set here, as a breakpoint resets it.
		s.options.swipe = false;
		s.options.draggable = false;

		const current = window.getComputedStyle(t).transform;

		s.slickwprevPause();

		// transition first, or pinning the transform animates it.
		t.style.transition = 'none';
		t.style.transform = current === 'none' ? target : current;

		setPosition = s.setPosition;
		s.setPosition = () => {};

		frozen = true;
	}

	/** Track position as slick's getLeft(index) = origin + index * step. */
	function geometry() {
		const s = slick();

		if (typeof s?.getLeft !== 'function' || !s.slideCount) {
			return null;
		}

		const origin = s.getLeft(0);
		const step = s.getLeft(1) - origin;

		return step ? { origin, step, count: s.slideCount } : null;
	}

	/** Manual scroll. Wraps inside the real slides; the clones hide the seam. */
	function moveBy(dx) {
		const g = geometry();

		freeze();

		const t = track();

		if (!frozen || !t || !g) {
			return;
		}

		const index = (translateX(t.style.transform) + dx - g.origin) / g.step;
		const wrapped = ((index % g.count) + g.count) % g.count;

		t.style.transform = translate(g.origin + wrapped * g.step);
		moved = true;
	}

	function settle(s, t) {
		t.style.transition = '';
		t.style.transform = '';
		s.setPosition();

		if (!paused && !hovered) {
			s.slickwprevPlay();
		}
	}

	function thaw() {
		const s = slick();
		const t = track();

		if (!frozen) {
			return;
		}

		frozen = false;

		if (!s || !t) {
			return;
		}

		if (setPosition) {
			s.setPosition = setPosition;
			setPosition = null;
		}

		// Carry on to the next slide boundary ahead, and tell slick it is there.
		const g = moved ? geometry() : null;

		moved = false;

		if (g) {
			const index = Math.ceil((translateX(t.style.transform) - g.origin) / g.step - 0.01);

			s.currentSlide = index % g.count;
			target = translate(g.origin + index * g.step);
		}

		const span = (s.slideWidth || 0) * (s.options?.slidesToScroll || 1);
		const left = span > 0
			? Math.min(Math.abs(translateX(target) - translateX(window.getComputedStyle(t).transform)) / span, 1)
			: 0;
		const ms = Math.round((s.options?.speed || 0) * left);

		if (ms < 50) {
			settle(s, t);

			return;
		}

		// linear, not the instance's cssEase: an easing curve restarted from a
		// standstill re-accelerates, which is exactly the jolt we're avoiding.
		t.style.transition = `transform ${ms}ms linear`;
		t.style.transform = target;

		window.clearTimeout(runout);
		runout = window.setTimeout(() => {
			if (!frozen) {
				settle(s, t);
			}
		}, ms);
	}

	function sync() {
		if (hovered || held || paused) {
			freeze();
		} else {
			thaw();
		}
	}

	function label() {
		if (!button) {
			return;
		}

		button.dataset.playing = paused ? 'false' : 'true';
		button.setAttribute('aria-label', paused ? button.dataset.labelPlay : button.dataset.labelPause);
	}

	// Pointer, not mouse, events: a tap fires mouseenter with no mouseleave.
	el.addEventListener('pointerenter', (e) => {
		if (e.pointerType !== 'touch') {
			hovered = true;
			sync();
		}
	});

	el.addEventListener('pointerleave', (e) => {
		if (e.pointerType !== 'touch') {
			hovered = false;
			sync();
		}
	});

	el.addEventListener('wheel', (e) => {
		const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;

		if (!dx || (!e.shiftKey && Math.abs(e.deltaX) < Math.abs(e.deltaY))) {
			return;
		}

		// Also stops the browser's swipe-to-go-back.
		e.preventDefault();
		moveBy(-dx * (e.deltaMode === 1 ? 16 : 1));
	}, { passive: false });

	el.addEventListener('pointerdown', (e) => {
		if (e.pointerType !== 'touch' && e.button !== 0) {
			return;
		}

		window.clearTimeout(resume);
		held = true;
		drag = { id: e.pointerId, type: e.pointerType, x: e.clientX, start: e.clientX, dragged: false };
		sync();
	});

	el.addEventListener('pointermove', (e) => {
		if (drag?.id !== e.pointerId) {
			return;
		}

		if (drag.type !== 'touch' && !drag.dragged) {
			if (Math.abs(e.clientX - drag.start) < DRAG_THRESHOLD) {
				return;
			}

			// Keep the drag when the pointer leaves the slider.
			drag.dragged = true;
			el.setPointerCapture(e.pointerId);
			el.classList.add('is-dragging');
			window.getSelection()?.removeAllRanges();
		}

		moveBy(e.clientX - drag.x);
		drag.x = e.clientX;
	});

	const release = (e) => {
		if (drag?.id !== e.pointerId) {
			return;
		}

		const { type, dragged } = drag;

		drag = null;
		el.classList.remove('is-dragging');

		// A drag ends on the slide it started on; don't let that count as a click.
		if (dragged) {
			const swallow = (click) => {
				click.preventDefault();
				click.stopPropagation();
			};

			// The click follows pointerup in the same task; drop the guard after it.
			el.addEventListener('click', swallow, { capture: true, once: true });
			window.setTimeout(() => el.removeEventListener('click', swallow, true), 0);
		}

		// Mouse: the hover keeps it paused until the pointer leaves.
		if (type !== 'touch') {
			held = false;
			sync();

			return;
		}

		resume = window.setTimeout(() => {
			held = false;
			sync();
		}, RESUME_AFTER);
	};

	// Stops the browser dragging an image or link instead of the slider.
	el.addEventListener('dragstart', (e) => e.preventDefault());

	// pointercancel: the browser took the gesture for a vertical page scroll.
	el.addEventListener('pointerup', release);
	el.addEventListener('pointercancel', release);

	button?.addEventListener('click', () => {
		paused = !paused;
		label();
		sync();
	});

	label();
	sync();
}

const apply = () => {
	const $ = window.jQuery;

	if (!$) {
		return;
	}

	$(SLIDERS).each(function () {
		const slider = $(this);

		// Nothing to set until the plugin has built the slider.
		if (!slider.hasClass('slickwprev-initialized')) {
			return;
		}

		// false: do not refresh. A refresh re-lays out the track and jumps the
		// crawl back to a slide boundary.
		slider.slickwprev('slickwprevSetOption', 'speed', SMALL.matches ? SPEED.small : SPEED.large, false);

		// Not a live region — slick puts aria-live on its track, and a crawling
		// carousel announcing itself is exactly what a11y audits flag.
		this.querySelectorAll('[aria-live]').forEach((node) => node.removeAttribute('aria-live'));

		if (!started.has(this)) {
			started.add(this);
			control(this);
		}
	});
};

// The plugin initialises on DOM ready, so `load` is always after it.
window.addEventListener('load', apply);
SMALL.addEventListener('change', apply);
